// src/components/admin/blog/ImageUploader.jsx - Complete Fixed Version
import React, { useCallback, useState, useEffect, useRef } from 'react';
import {
  FiUpload,
  FiX,
  FiImage,
  FiEdit2,
  FiTrash2,
  FiRefreshCw,
  FiCheckCircle,
  FiAlertCircle,
  FiMaximize,
  FiMinimize,
  FiZoomIn,
  FiZoomOut,
  FiRotateCw,
  FiGrid,
} from 'react-icons/fi';
import { MdOutlineAspectRatio } from 'react-icons/md';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';

// ============================================
// IMAGE RATIO PRESETS
// ============================================

const RATIO_PRESETS = [
  { label: '16:9', value: '16:9', width: 16, height: 9 },
  { label: '4:3', value: '4:3', width: 4, height: 3 },
  { label: '1:1', value: '1:1', width: 1, height: 1 },
  { label: '21:9', value: '21:9', width: 21, height: 9 },
  { label: '3:2', value: '3:2', width: 3, height: 2 },
  { label: '5:4', value: '5:4', width: 5, height: 4 },
];

// ============================================
// IMAGE SIZE VALIDATION
// ============================================

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MIN_FILE_SIZE = 5 * 1024; // 5KB
const RECOMMENDED_WIDTH = 1920;
const RECOMMENDED_HEIGHT = 1080;

// ============================================
// MAIN COMPONENT
// ============================================

export const ImageUploader = ({
  onImageUpload,
  existingImage,
  onRemoveImage,
  className = '',
  disabled = false,
  showMetadata = true,
  showCropGuide = true,
  showRatioSelector = true,
  isUploading = false,
}) => {
  // ===== STATE =====
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(existingImage || '');
  const [selectedRatio, setSelectedRatio] = useState('16:9');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [imageMetadata, setImageMetadata] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showZoom, setShowZoom] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imageLoaded, setImageLoaded] = useState(false);

  // ===== REFS =====
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);

  // ===== EFFECTS =====
  useEffect(() => {
    if (existingImage && existingImage !== preview) {
      setPreview(existingImage);
      // Load image metadata
      if (existingImage) {
        const img = new Image();
        img.onload = () => {
          setImageMetadata({
            width: img.width,
            height: img.height,
            ratio: (img.width / img.height).toFixed(2),
            size: 'Loaded',
          });
          setImageLoaded(true);
        };
        img.onerror = () => {
          console.error('Failed to load image:', existingImage);
          setError('Failed to load image. Please try again.');
        };
        img.src = existingImage;
      }
    }
  }, [existingImage]);

  // ===== HANDLERS =====

  const validateImage = file => {
    // Check file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (JPG, PNG, WEBP, etc.)');
      return false;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      setError(
        `Image size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds 10MB limit`,
      );
      return false;
    }

    if (file.size < MIN_FILE_SIZE) {
      setError('Image file is too small. Minimum size is 5KB');
      return false;
    }

    return true;
  };

  const loadImageMetadata = file => {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = e => {
        const img = new Image();
        img.onload = () => {
          resolve({
            width: img.width,
            height: img.height,
            ratio: (img.width / img.height).toFixed(2),
            size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
            name: file.name,
            type: file.type,
          });
        };
        img.onerror = () => {
          resolve({
            width: 0,
            height: 0,
            ratio: '0',
            size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
            name: file.name,
            type: file.type,
          });
        };
        img.src = e.target.result;
      };
      reader.onerror = () => {
        resolve({
          width: 0,
          height: 0,
          ratio: '0',
          size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
          name: file.name,
          type: file.type,
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDrop = useCallback(
    async acceptedFiles => {
      if (!acceptedFiles || acceptedFiles.length === 0) {
        setError('No file selected');
        return;
      }

      const file = acceptedFiles[0];
      if (!file) {
        setError('Invalid file');
        return;
      }

      // Reset states
      setError(null);
      setUploading(true);
      setUploadProgress(0);
      setImageLoaded(false);

      // Validate
      if (!validateImage(file)) {
        setUploading(false);
        return;
      }

      try {
        // Read file as data URL for preview
        const reader = new FileReader();

        // Simulate upload progress
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev >= 95) {
              clearInterval(progressInterval);
              return 95;
            }
            return Math.min(prev + Math.random() * 15 + 5, 95);
          });
        }, 100);

        reader.onloadend = async () => {
          const dataUrl = reader.result;
          if (!dataUrl) {
            clearInterval(progressInterval);
            setError('Failed to read file');
            setUploading(false);
            return;
          }

          setPreview(dataUrl);

          // Load metadata
          try {
            const metadata = await loadImageMetadata(file);
            setImageMetadata(metadata);
          } catch (metaError) {
            console.warn('Failed to load metadata:', metaError);
            // Continue even if metadata fails
          }

          setImageLoaded(true);

          // Complete upload
          clearInterval(progressInterval);
          setUploadProgress(100);

          // Call parent with file and preview
          if (onImageUpload) {
            onImageUpload(file, dataUrl);
          }

          // Reset after a moment
          setTimeout(() => {
            setUploading(false);
            setUploadProgress(0);
          }, 500);
        };

        reader.onerror = () => {
          clearInterval(progressInterval);
          setError('Failed to read file');
          setUploading(false);
        };

        reader.readAsDataURL(file);
      } catch (err) {
        console.error('Upload error:', err);
        setError('Failed to upload image. Please try again.');
        setUploading(false);
        setUploadProgress(0);
      }
    },
    [onImageUpload],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.svg', '.bmp'],
    },
    maxFiles: 1,
    disabled: disabled || uploading || isUploading,
  });

  const handleRemove = useCallback(() => {
    setPreview('');
    setImageMetadata(null);
    setImageLoaded(false);
    setUploading(false);
    setUploadProgress(0);
    setError(null);
    setZoomLevel(1);
    if (onRemoveImage) {
      onRemoveImage();
    }
  }, [onRemoveImage]);

  const handleRatioChange = ratio => {
    setSelectedRatio(ratio);
  };

  const getRatioStyle = () => {
    const preset = RATIO_PRESETS.find(r => r.value === selectedRatio);
    if (!preset) return { aspectRatio: '16 / 9' };
    return { aspectRatio: `${preset.width} / ${preset.height}` };
  };

  // ===== RENDER HELPERS =====

  const renderMetadata = () => {
    if (!imageMetadata || !showMetadata) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs"
      >
        <div className="px-3 py-2 rounded-lg bg-black/5 dark:bg-white/5">
          <span className="block opacity-60">Dimensions</span>
          <span className="font-medium">
            {imageMetadata.width || '?'}×{imageMetadata.height || '?'}
          </span>
        </div>
        <div className="px-3 py-2 rounded-lg bg-black/5 dark:bg-white/5">
          <span className="block opacity-60">Aspect Ratio</span>
          <span className="font-medium">{imageMetadata.ratio || '?'}:1</span>
        </div>
        <div className="px-3 py-2 rounded-lg bg-black/5 dark:bg-white/5">
          <span className="block opacity-60">File Size</span>
          <span className="font-medium">{imageMetadata.size || 'Unknown'}</span>
        </div>
        <div className="px-3 py-2 rounded-lg bg-black/5 dark:bg-white/5">
          <span className="block opacity-60">Format</span>
          <span className="font-medium">
            {imageMetadata.type?.split('/')[1]?.toUpperCase() || 'Unknown'}
          </span>
        </div>
      </motion.div>
    );
  };

  const renderCropGuide = () => {
    if (!showCropGuide || !preview || isDragActive || uploading) return null;

    return (
      <div className="absolute inset-0 pointer-events-none">
        {/* Rule of thirds grid */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-full h-full relative">
            <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/20" />
            <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/20" />
            <div className="absolute top-1/3 left-0 right-0 h-px bg-white/20" />
            <div className="absolute top-2/3 left-0 right-0 h-px bg-white/20" />

            {/* Center crosshair */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8">
              <div className="absolute top-1/2 left-0 right-0 h-px bg-white/30" />
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/30" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full border border-white/30" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderUploadProgress = () => {
    if (!uploading && uploadProgress === 0) return null;

    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="w-48 max-w-full text-center">
          <div className="relative w-full h-1.5 rounded-full overflow-hidden bg-white/20">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-600"
              initial={{ width: '0%' }}
              animate={{ width: `${Math.min(uploadProgress, 100)}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <p className="mt-3 text-sm font-medium text-white">
            Uploading... {Math.round(Math.min(uploadProgress, 100))}%
          </p>
          <p className="mt-1 text-xs text-white/60">
            Please wait while your image is being processed
          </p>
        </div>
      </div>
    );
  };

  const renderError = () => {
    if (!error) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-3 px-4 py-2.5 rounded-lg bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 text-sm flex items-center gap-2"
      >
        <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
        <span>{error}</span>
        <button
          onClick={() => setError(null)}
          className="ml-auto p-1 rounded hover:bg-red-200 dark:hover:bg-red-800/30 transition-colors"
        >
          <FiX className="w-3.5 h-3.5" />
        </button>
      </motion.div>
    );
  };

  const renderRatioSelector = () => {
    if (!showRatioSelector || preview) return null;

    return (
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-xs opacity-60 flex items-center gap-1">
          <MdOutlineAspectRatio className="w-3.5 h-3.5" />
          Aspect Ratio:
        </span>
        <div className="flex flex-wrap gap-1.5">
          {RATIO_PRESETS.map(ratio => (
            <button
              key={ratio.value}
              onClick={() => handleRatioChange(ratio.value)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                selectedRatio === ratio.value
                  ? 'bg-black text-white dark:bg-white dark:text-black shadow-md'
                  : 'bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10'
              }`}
            >
              {ratio.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // ===== MAIN RENDER =====

  const isDisabled = disabled || uploading || isUploading;

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Dropzone Area */}
      <div
        ref={containerRef}
        {...getRootProps()}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        className={`
          relative rounded-xl overflow-hidden transition-all duration-300
          ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${isDragActive ? 'ring-2 ring-blue-500 ring-offset-2' : ''}
          ${preview ? 'border border-gray-200 dark:border-gray-700' : 'border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'}
        `}
        style={preview ? getRatioStyle() : { aspectRatio: '16 / 9' }}
      >
        <input ref={fileInputRef} {...getInputProps()} disabled={isDisabled} />

        {/* Preview Image */}
        <AnimatePresence mode="wait">
          {preview ? (
            <motion.div
              key="preview"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="relative w-full h-full"
            >
              <img
                ref={imgRef}
                src={preview}
                alt="Upload preview"
                className={`w-full h-full object-cover transition-transform duration-300 ${
                  isHovering && !uploading && !isUploading
                    ? 'scale-105'
                    : 'scale-100'
                }`}
                style={{ transform: `scale(${zoomLevel})` }}
                onLoad={() => setImageLoaded(true)}
                onError={() => {
                  setError('Failed to load image preview');
                  setImageLoaded(false);
                }}
              />

              {/* Crop Guide Overlay */}
              {renderCropGuide()}

              {/* Upload Progress Overlay */}
              {(uploading || isUploading) && renderUploadProgress()}

              {/* Hover Actions */}
              {!uploading && !isUploading && !disabled && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: isHovering ? 1 : 0 }}
                  className="absolute inset-0 flex items-center justify-center gap-3 bg-black/40 backdrop-blur-sm transition-opacity duration-300"
                >
                  {/* Zoom In */}
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setZoomLevel(Math.min(zoomLevel + 0.25, 2));
                    }}
                    className="p-2.5 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors transform hover:scale-110"
                    title="Zoom In"
                  >
                    <FiZoomIn className="w-4 h-4" />
                  </button>

                  {/* Zoom Out */}
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setZoomLevel(Math.max(zoomLevel - 0.25, 0.5));
                    }}
                    className="p-2.5 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors transform hover:scale-110"
                    title="Zoom Out"
                  >
                    <FiZoomOut className="w-4 h-4" />
                  </button>

                  {/* Reset Zoom */}
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setZoomLevel(1);
                    }}
                    className="p-2.5 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors transform hover:scale-110"
                    title="Reset Zoom"
                  >
                    <FiMaximize className="w-4 h-4" />
                  </button>

                  {/* Remove Image */}
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      handleRemove();
                    }}
                    className="p-2.5 rounded-full bg-red-500/80 hover:bg-red-600 text-white transition-colors transform hover:scale-110"
                    title="Remove Image"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </motion.div>
              )}

              {/* Status Badge */}
              {(uploading || isUploading) && (
                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-500 text-white animate-pulse">
                  Uploading...
                </div>
              )}

              {!uploading && !isUploading && imageLoaded && (
                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md text-xs font-medium bg-green-500/90 text-white flex items-center gap-1">
                  <FiCheckCircle className="w-3 h-3" />
                  Uploaded
                </div>
              )}
            </motion.div>
          ) : (
            // Empty State
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center w-full h-full p-6"
            >
              {uploading || isUploading ? (
                <div className="flex flex-col items-center gap-3">
                  <FiRefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                  <p
                    className="text-sm font-medium"
                    style={{ color: 'var(--text)' }}
                  >
                    Processing image...
                  </p>
                </div>
              ) : isDragActive ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <FiUpload className="w-8 h-8 text-blue-500" />
                  </div>
                  <p className="text-sm font-medium text-blue-500">
                    Drop your image here
                  </p>
                </div>
              ) : (
                <>
                  <div className="w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                    <FiImage
                      className="w-10 h-10 opacity-40"
                      style={{ color: 'var(--muted)' }}
                    />
                  </div>
                  <p
                    className="text-sm font-medium"
                    style={{ color: 'var(--text)' }}
                  >
                    Click or drag to upload
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                    JPG, PNG, WEBP, SVG • Up to 10MB
                  </p>
                  <p
                    className="text-xs mt-2 px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800"
                    style={{ color: 'var(--muted)' }}
                  >
                    Recommended: 1920×1080px (16:9)
                  </p>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Ratio Selector */}
      {renderRatioSelector()}

      {/* Image Metadata */}
      {renderMetadata()}

      {/* Error Display */}
      {renderError()}

      {/* Quick Tips */}
      {!preview && !error && !uploading && !isUploading && (
        <div
          className="flex flex-wrap items-center gap-4 text-xs"
          style={{ color: 'var(--muted)' }}
        >
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            Max 10MB
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
            16:9 Recommended
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
            Drag & Drop
          </span>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
